/**
 * Operation Service - Frontend
 * @description Service for operation-related API operations
 * Optimized for operational dashboard flow
 * Uses mappers to transform backend data (shortened fields) to frontend data (full fields)
 */

import { api } from '../config';
import { ApiResponse, OperationResponse, OperationQueryParams } from '@/types';
import { BackendApiResponse, BackendOperation } from '@/types/backend.types';
import { OperationMapper } from '@/mappers/operation.mapper';
import { ApiMapper } from '@/mappers/api.mapper';

export const operationService = {
  /**
   * Get all operations with optional pagination and filters
   * For admin users or general operation listing
   */
  async getAll(params?: OperationQueryParams): Promise<ApiResponse<OperationResponse[]>> {
    const response = await api.get<BackendApiResponse<BackendOperation[]>>('/operations', {
      params,
    });
    return ApiMapper.mapResponseArray(response.data, OperationMapper.toFrontend);
  },

  /**
   * Get operation by ID
   * For operation details page
   */
  async getById(id: string): Promise<ApiResponse<OperationResponse>> {
    const response = await api.get<BackendApiResponse<BackendOperation>>(`/operations/${id}`);
    return ApiMapper.mapResponse(response.data, OperationMapper.toFrontend);
  },

  /**
   * ⭐ CORE: Get operations by device ID
   * For Tabla 2: Operations of a specific device
   * Called from OperationsDetailPage when user selects a device
   */
  async getByDeviceId(
    deviceId: string,
    params?: OperationQueryParams
  ): Promise<ApiResponse<OperationResponse[]>> {
    const response = await api.get<BackendApiResponse<BackendOperation[]>>(
      `/operations/device/${deviceId}`,
      { params }
    );
    return ApiMapper.mapResponseArray(response.data, OperationMapper.toFrontend);
  },

  /**
   * Get operation by operation identifier
   * For specific operation lookup
   */
  async getByOperationIdentifier(identifier: string): Promise<ApiResponse<OperationResponse>> {
    const response = await api.get<BackendApiResponse<BackendOperation>>(
      `/operations/identifier/${identifier}`
    );
    return ApiMapper.mapResponse(response.data, OperationMapper.toFrontend);
  },

  /**
   * Get operations by date range
   * For filtering operations by time period
   */
  async getByDateRange(
    startDate: string,
    endDate: string,
    params?: OperationQueryParams
  ): Promise<ApiResponse<OperationResponse[]>> {
    const queryParams = {
      ...params,
      startDate,
      endDate,
    };
    const response = await api.get<BackendApiResponse<BackendOperation[]>>(
      '/operations/date-range',
      {
        params: queryParams,
      }
    );
    return ApiMapper.mapResponseArray(response.data, OperationMapper.toFrontend);
  },

  /**
   * Get active operations
   * For real-time monitoring
   */
  async getActiveOperations(
    params?: OperationQueryParams
  ): Promise<ApiResponse<OperationResponse[]>> {
    const response = await api.get<BackendApiResponse<BackendOperation[]>>('/operations/active', {
      params,
    });
    return ApiMapper.mapResponseArray(response.data, OperationMapper.toFrontend);
  },

  /**
   * Create new operation
   * For future functionality if needed
   */
  async create(operationData: Partial<OperationResponse>): Promise<ApiResponse<OperationResponse>> {
    const backendData = ApiMapper.mapRequestData(operationData, OperationMapper.toBackend);
    const response = await api.post<BackendApiResponse<BackendOperation>>(
      '/operations',
      backendData
    );
    return ApiMapper.mapResponse(response.data, OperationMapper.toFrontend);
  },

  /**
   * Update operation
   * For future functionality if needed
   */
  async update(
    id: string,
    operationData: Partial<OperationResponse>
  ): Promise<ApiResponse<OperationResponse>> {
    const backendData = ApiMapper.mapRequestData(operationData, OperationMapper.toBackend);
    const response = await api.put<BackendApiResponse<BackendOperation>>(
      `/operations/${id}`,
      backendData
    );
    return ApiMapper.mapResponse(response.data, OperationMapper.toFrontend);
  },

  /**
   * Delete operation
   * For future functionality if needed
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<BackendApiResponse<void>>(`/operations/${id}`);

    if (response.data.success) {
      return {
        success: true,
        data: undefined as void,
        message: response.data.message,
      };
    } else {
      return {
        success: false,
        error: response.data.error!,
        message: response.data.message,
      };
    }
  },
};
